import { faListAlt } from '@fortawesome/free-regular-svg-icons';
import {
  faGraduationCap,
  faHandshake,
  faShoppingCart,
  faUserGraduate,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { useEffect, useMemo, useState } from 'react';
import { Container, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useGaming } from '../../../contexts/GamingContext';
import { useGeolocation } from '../../../contexts/GeolocationContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { canAccessFeature, canEnterEcm, canSeeShop, isItalian } from '../../utils/utils';
import { LoginModal } from '../LoginModal/LoginModal';
import { Logo } from '../Logo/Logo';
import { TopbarActions } from './TopbarActions';
import { ButtonDefinition, TopbarButton } from './TopbarButton';
import { TopbarMobileMenu } from './TopbarMobileMenu';
import { TopbarSearch } from './TopbarSearch';
import styles from './Topbar.module.scss';

/** Mostra l'header condiviso e compone i relativi elementi di navigazione desktop e mobile. */
const TopbarComponent = ({ bg = 'transparent', variant = 'dark', inHeader = false, showLoginButtonIfNeeded = true, showSubscribeButtonIfNeeded = true, showSearchBarIfNeeded = true }) => {
  const { isLoggedIn, notificationBadge, cleanNotifications, subscription, userDataFmt, staticData, userCompanyData } = useFirebase();
  const { isEnabled: isGamingEnabled } = useGaming();
  const { isNative } = useCapacitor();
  const { isMobile } = useMobileDetect();
  const { windowWidth } = useWindowSize();
  const isDesktopViewport = windowWidth >= 992;
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pagePath, setPagePath] = useState<string>(null);
  const [isAso, setIsAso] = useState(false);
  const [companyId, setCompanyId] = useState<string>(null);
  const [companyName, setCompanyName] = useState<string>(null);
  const [isCompanyEnabled, setIsCompanyEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const { t } = useTranslation();
  const { country_code } = useGeolocation();
  const { currentLanguage } = useCurrentLanguage();
  const history = useHistory();

  const buttons: ButtonDefinition[] = useMemo(
    () => [
      {
        url: '/browse',
        icon: faVideo,
        label: t('labels.video'),
      },
      {
        url: '/courses',
        icon: faGraduationCap,
        label: t('labels.corsi'),
         condition: () => !!isAso && isItalian(country_code),
      },
      {
        url: '/list',
        icon: faListAlt,
        label: t('labels.mieiToot'),
      },
      {
        url: '/tootors',
        icon: faUserGraduate,
        label: t('labels.tootors'),
        condition: () => !!staticData.SHOW_TOOTORS_LIST,
      },
      {
        url: `/company/${companyId}/courses`,
        icon: faHandshake,
        label: companyName,
          condition: () => !!staticData.ENABLE_COMPANIES && companyId != null && isCompanyEnabled,
      },
      {
        externalTokenizedUrl: true,
        icon: faGraduationCap,
        label: 'Accedi a ECM',
        condition: () =>
          canAccessFeature('ECM', currentLanguage, staticData, userDataFmt) && canEnterEcm(subscription, userDataFmt) && !isAso && currentLanguage === 'it',
      },
      {
        url: '/shop',
        icon: faShoppingCart,
        label: 'Corsi ECM',
        condition: () => {
          return (
            (!isLoggedIn && currentLanguage == 'it') ||
            (canAccessFeature('SHOP', currentLanguage, staticData, userDataFmt) && canSeeShop(subscription, userDataFmt, staticData, currentLanguage))
          );
        },
      },
    ],
    [isAso, country_code, isGamingEnabled, staticData, userDataFmt, currentLanguage, companyName, isCompanyEnabled, isLoggedIn, subscription]
  );

  useEffect(() => {
    setPagePath(window.location.pathname);
  }, []);

  useEffect(() => {
    const updateScrolledState = () => setHasScrolled(window.scrollY > 8);
    updateScrolledState();
    window.addEventListener('scroll', updateScrolledState);
    return () => window.removeEventListener('scroll', updateScrolledState);
  }, []);

  useEffect(() => {
    if (!userDataFmt) return;
    const myCategory = userDataFmt.profile?.formData?.categoriaProfessionale;
    setIsAso(myCategory == 'aso');
  }, [userDataFmt]);

  useEffect(() => {
    if (!userCompanyData) return;
    const companyId = userCompanyData?.id;
    const companyName = userCompanyData?.name;
    const isCompanyEnabled = !!userCompanyData?.enabled;

    setCompanyId(companyId);
    setCompanyName(companyName);
    setIsCompanyEnabled(isCompanyEnabled);
  }, [userCompanyData]);

  const submitSearch = (event) => {
    event.preventDefault();
    history.push(searchTerm ? `/search?q=${encodeURIComponent(searchTerm)}` : '/search');
  };

  return (
    <>
      <div className={styles.topbar}>
        <div className={`${styles.topbarInner} ${hasScrolled ? styles.scrolled : ''}`}>
          <Navbar data-testid="topbar" className={styles.tootorAppBar} bg={bg} variant={variant as 'dark' | 'light'} sticky={'top'}>
            <Container fluid>
              <Navbar.Brand>
                <TopbarMobileMenu isLoggedIn={isLoggedIn} isNative={isNative} buttons={buttons} pagePath={pagePath} />
                {!inHeader && <Logo className={`${styles.logoTootor} d-inline d-lg-none`} variant={'reduced-light'} clickable />}
                {!inHeader && <Logo className={`${styles.logoTootor} d-none d-lg-inline`} variant={'light'} clickable />}
                {!isLoggedIn && currentLanguage === 'it' && (
                  <div className={'d-inline-flex align-items-center ml-4'}>
                    <div className={'d-none d-md-block'}>
                      {/* Mostra il collegamento pubblico allo shop quando l'utente non ha effettuato l'accesso. */}
                      {(!isLoggedIn && currentLanguage == 'it') ||
                      (canAccessFeature('SHOP', currentLanguage, staticData, userDataFmt) && canSeeShop(subscription, userDataFmt, staticData, currentLanguage)) ? (
                        <TopbarButton
                          pagePath={pagePath}
                          buttonDefinition={{
                            url: '/shop',
                            icon: faShoppingCart,
                            label: 'Acquista ECM',
                            condition: () => currentLanguage === 'it',
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                )}
              </Navbar.Brand>
              {isLoggedIn && !isNative && showSearchBarIfNeeded && (
                <TopbarSearch
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onSubmit={submitSearch}
                  placeholder={t('labels.cerca')}
                />
              )}
              <TopbarActions
                isLoggedIn={isLoggedIn}
                isMobile={isMobile}
                isDesktopViewport={isDesktopViewport}
                isNative={isNative}
                isGamingEnabled={isGamingEnabled}
                subscription={subscription}
                userDataFmt={userDataFmt}
                notificationBadge={notificationBadge}
                cleanNotifications={cleanNotifications}
                showLoginButtonIfNeeded={showLoginButtonIfNeeded}
                showSubscribeButtonIfNeeded={showSubscribeButtonIfNeeded}
                onLogin={() => setIsLoginModalOpen(true)}
              />
            </Container>
          </Navbar>
        </div>
      </div>

      <LoginModal isModalOpen={isLoginModalOpen} setIsModalOpen={setIsLoginModalOpen} />
    </>
  );
};

export { TopbarComponent as Topbar };
